import { HeaderPage } from "@/components/HeaderPage";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { IconPicker } from "@/components/IconPicker";
import { CategoryIcon } from "@/components/CategoryIcon";
import {
  Card,
  CardContent as CardContentUI,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Clock,
  Save,
  Tag,
  Edit,
  Trash2,
  Plus,
  X,
  FileText,
  MessageSquare,
  History,
  User,
  MapPin,
  PlusCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCreateCategory,
  useCreateSubCategory,
  useGetCategories,
  useDeleteCategory,
  useDeleteSubCategory,
  useUpdateCategory,
  useUpdateSubCategory,
} from "@/hooks/useCategories";
import {
  useCreateConfiguration,
  useUpdateConfiguration,
  useGetConfiguration,
} from "@/hooks/useConfiguration";
import {
  useCreateMessageTemplate,
  useGetMessageTemplates,
  useUpdateMessagesTemplates,
  useDeleteMessageTemplate,
} from "@/hooks/useMessageTemplate";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import type { Category } from "@/types/Category";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  configurationSchema,
  type ConfigurationFormData,
} from "@/schemas/configurationSchemas";
import { Loading } from "@/components/Loading";
import { ConfigurationHistoryDialog } from "@/components/Dialogs/ConfigurationHistoryDialog";

// ─── CategoryModal ─────────────────────────────────────────────────────────────
type SubCategoryItem = { id?: string; name: string; description: string };

interface CategoryModalProps {
  open: boolean;
  editingCategory: Category | null;
  onClose: () => void;
  onSaved: () => void;
}

const CategoryModal = ({ open, editingCategory, onClose, onSaved }: CategoryModalProps) => {
  const queryClient = useQueryClient();

  const [categoryName, setCategoryName] = useState(editingCategory?.name ?? "");
  const [categoryDescription, setCategoryDescription] = useState(editingCategory?.description ?? "");
  const [categoryIcon, setCategoryIcon] = useState(editingCategory?.icon ?? "");
  const [categoryLib, setCategoryLib] = useState(editingCategory?.lib ?? "lucide-react");
  const [subCategories, setSubCategories] = useState<SubCategoryItem[]>(
    editingCategory?.subcategories?.map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description ?? "",
    })) ?? [],
  );
  const [newSubCategoryName, setNewSubCategoryName] = useState("");
  const [newSubCategoryDescription, setNewSubCategoryDescription] = useState("");

  const createCategoryMutation = useCreateCategory();
  const createSubCategoryMutation = useCreateSubCategory();
  const updateCategoryMutation = useUpdateCategory();
  const updateSubCategoryMutation = useUpdateSubCategory();
  const deleteSubCategoryMutation = useDeleteSubCategory();

  // Sync state when editingCategory changes (modal opened for a different category)
  useEffect(() => {
    setCategoryName(editingCategory?.name ?? "");
    setCategoryDescription(editingCategory?.description ?? "");
    setCategoryIcon(editingCategory?.icon ?? "");
    setCategoryLib(editingCategory?.lib ?? "lucide-react");
    setSubCategories(
      editingCategory?.subcategories?.map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description ?? "",
      })) ?? [],
    );
    setNewSubCategoryName("");
    setNewSubCategoryDescription("");
  }, [editingCategory, open]);

  const handleClose = () => {
    onClose();
  };

  const handleAddSubCategory = () => {
    if (newSubCategoryName.trim()) {
      setSubCategories((prev) => [...prev, { name: newSubCategoryName, description: newSubCategoryDescription }]);
      setNewSubCategoryName("");
      setNewSubCategoryDescription("");
    }
  };

  const handleUpdateSubCategoryField = (index: number, field: "name" | "description", value: string) => {
    setSubCategories((prev) => prev.map((sub, i) => (i === index ? { ...sub, [field]: value } : sub)));
  };

  const handleRemoveSubCategory = async (index: number) => {
    const subCategory = subCategories[index];
    if (subCategory.id) {
      try {
        await deleteSubCategoryMutation.mutateAsync(subCategory.id);
        toast.success("Subcategoria removida com sucesso!");
        queryClient.invalidateQueries({ queryKey: ["getCategories"] });
      } catch {
        toast.error("Erro ao remover subcategoria");
        return;
      }
    }
    setSubCategories((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!categoryName.trim()) {
      toast.error("O nome da categoria é obrigatório");
      return;
    }
    try {
      if (editingCategory) {
        await updateCategoryMutation.mutateAsync({
          id: editingCategory.id,
          name: categoryName,
          description: categoryDescription,
          icon: categoryIcon,
          lib: categoryLib,
          isActive: editingCategory.isActive ?? true,
        });
        for (const sub of subCategories) {
          if (sub.id) {
            await updateSubCategoryMutation.mutateAsync({ id: sub.id, name: sub.name, description: sub.description });
          } else if (sub.name.trim()) {
            await createSubCategoryMutation.mutateAsync({ categoryId: editingCategory.id, name: sub.name, description: sub.description });
          }
        }
        toast.success("Categoria atualizada com sucesso!");
      } else {
        const created = await createCategoryMutation.mutateAsync({ name: categoryName, description: categoryDescription, icon: categoryIcon, lib: categoryLib });
        if (subCategories.length > 0 && created?.id) {
          for (const sub of subCategories) {
            await createSubCategoryMutation.mutateAsync({ categoryId: created.id, name: sub.name, description: sub.description });
          }
        }
        toast.success("Categoria criada com sucesso!");
      }
      queryClient.invalidateQueries({ queryKey: ["getCategories"] });
      onSaved();
    } catch {
      toast.error("Erro ao salvar categoria");
    }
  };

  const isSaving =
    createCategoryMutation.isPending ||
    updateCategoryMutation.isPending ||
    updateSubCategoryMutation.isPending ||
    createSubCategoryMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingCategory ? "Editar Categoria" : "Adicionar Nova Categoria"}</DialogTitle>
          <DialogDescription>
            {editingCategory ? "Atualize as informações da categoria" : "Crie uma nova categoria e adicione subcategorias relacionadas"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Informações da Categoria */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="categoryName">
                  Nome da Categoria <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="categoryName"
                  placeholder="Ex: Iluminação Pública"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="categoryDescription">Descrição</Label>
                <Input
                  id="categoryDescription"
                  placeholder="Descrição da categoria (opcional)"
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                />
              </div>
            </div>

            <IconPicker
              label="Ícone da Categoria"
              value={categoryIcon}
              onChange={setCategoryIcon}
              iconLibrary={categoryLib}
              onLibraryChange={setCategoryLib}
            />
          </div>

          <Separator className="my-2" />

          {/* Subcategorias */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Subcategorias</h4>
              {editingCategory && (
                <Badge variant="outline" className="text-xs">
                  {subCategories.filter((sub) => sub.id).length} existentes
                </Badge>
              )}
            </div>

            {subCategories.length > 0 && (
              <div className="space-y-2">
                {subCategories.map((sub, index) => (
                  <div key={sub.id || index} className="flex items-start gap-2 p-3 border rounded-md bg-muted/30">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground">Nome</Label>
                          {!sub.id && <Badge variant="secondary" className="text-xs">Nova</Badge>}
                        </div>
                        <Input
                          value={sub.name}
                          onChange={(e) => handleUpdateSubCategoryField(index, "name", e.target.value)}
                          placeholder="Nome da subcategoria"
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Descrição</Label>
                        <Input
                          value={sub.description}
                          onChange={(e) => handleUpdateSubCategoryField(index, "description", e.target.value)}
                          placeholder="Descrição (opcional)"
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveSubCategory(index)}
                      className="mt-5 text-destructive hover:text-destructive shrink-0"
                      disabled={deleteSubCategoryMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3 p-4 border border-dashed rounded-md">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="newSubCategoryName">Nome da Subcategoria</Label>
                  <Input
                    id="newSubCategoryName"
                    placeholder="Ex: Poste sem luz"
                    value={newSubCategoryName}
                    onChange={(e) => setNewSubCategoryName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newSubCategoryDescription">Descrição (opcional)</Label>
                  <Input
                    id="newSubCategoryDescription"
                    placeholder="Descrição (opcional)"
                    value={newSubCategoryDescription}
                    onChange={(e) => setNewSubCategoryDescription(e.target.value)}
                  />
                </div>
              </div>
              <Button variant="outline" onClick={handleAddSubCategory} className="w-full" type="button">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Subcategoria
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="cursor-pointer" onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="text-white cursor-pointer">
            {isSaving ? "Salvando..." : editingCategory ? "Atualizar Categoria" : "Criar Categoria"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
// ───────────────────────────────────────────────────────────────────────────────

export const Configuracoes = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);


  // Estados para templates de mensagens
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  // Estados para criar subcategoria
  const [isSubCategoryModalOpen, setIsSubCategoryModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [subCategoryName, setSubCategoryName] = useState("");
  const [subCategoryDescription, setSubCategoryDescription] = useState("");

  // Estados para deletar categoria/subcategoria
  const [categoryDeleteConfirmOpen, setCategoryDeleteConfirmOpen] =
    useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [subcategoryDeleteConfirmOpen, setSubcategoryDeleteConfirmOpen] =
    useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState<string | null>(
    null,
  );

  const createSubCategoryMutation = useCreateSubCategory();
  const deleteCategoryMutation = useDeleteCategory();
  const deleteSubCategoryMutation = useDeleteSubCategory();
  const { data: categoriesData, isLoading: loadingCategories } =
    useGetCategories();

  // Hooks de templates de mensagens
  const { data: templatesData, isLoading: loadingTemplates } =
    useGetMessageTemplates();
  const createTemplateMutation = useCreateMessageTemplate();
  const updateTemplateMutation = useUpdateMessagesTemplates();
  const deleteTemplateMutation = useDeleteMessageTemplate();

  // Hooks de configuração
  const { data: configurationData, isLoading: loadingConfiguration } =
    useGetConfiguration();
  const createConfigurationMutation = useCreateConfiguration();
  const updateConfigurationMutation = useUpdateConfiguration();

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ConfigurationFormData>({
    resolver: zodResolver(configurationSchema),
    defaultValues: {
      municipalityName: "",
      municipalityState: undefined,
      estimatedPopulation: 0,
      contactEmail: "",
      slaCriticalDays: 1,
      slaHighDays: 2,
      slaMediumDays: 5,
      slaLowDays: 15,
      deduplicationRadiusMeters: 100,
      deduplicationTimeWindowDays: 7,
      occurrenceReopeningDeadlineDays: 7,
    },
  });

  // Preenche o formulário quando os dados de configuração são carregados
  useEffect(() => {
    if (configurationData) {
      console.log("🔍 Dados de configuração recebidos:", configurationData);
      console.log("🔍 Estado recebido:", configurationData.municipalityState);

      reset({
        municipalityName: configurationData.municipalityName || "",
        municipalityState: configurationData.municipalityState || "",
        estimatedPopulation: configurationData.estimatedPopulation || 0,
        contactEmail: configurationData.contactEmail || "",
        slaCriticalDays: configurationData.slaCriticalDays || 1,
        slaHighDays: configurationData.slaHighDays || 2,
        slaMediumDays: configurationData.slaMediumDays || 5,
        slaLowDays: configurationData.slaLowDays || 15,
        deduplicationRadiusMeters:
          configurationData.deduplicationRadiusMeters || 100,
        deduplicationTimeWindowDays:
          configurationData.deduplicationTimeWindowDays || 7,
        occurrenceReopeningDeadlineDays:
          configurationData.occurrenceReopeningDeadlineDays || 7,
      });

      console.log("✅ Formulário resetado com dados da API");
    }
  }, [configurationData, reset]);

  // Garante que categories seja sempre um array
  const categories = Array.isArray(categoriesData)
    ? categoriesData
    : categoriesData?.data || categoriesData?.categories || [];

  // Garante que templates seja sempre um array
  const templates = Array.isArray(templatesData)
    ? templatesData
    : templatesData?.data || templatesData?.templates || [];



  // Função para salvar as configurações
  const onSubmitConfiguration = async (data: ConfigurationFormData) => {
    try {
      // Se já existir configuração, usa update, senão usa create
      if (configurationData?.id) {
        await updateConfigurationMutation.mutateAsync(data);
        toast.success("Configurações atualizadas com sucesso!");
      } else {
        await createConfigurationMutation.mutateAsync(data);
        toast.success("Configurações criadas com sucesso!");
      }

      // Invalida o cache para atualizar os dados
      queryClient.invalidateQueries({ queryKey: ["getConfiguration"] });
    } catch (error) {
      toast.error("Erro ao salvar configurações");
      console.error(error);
    }
  };

  // Funções para templates de mensagens
  const handleOpenTemplateModal = (template?: any) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateTitle(template.title);
      setTemplateContent(template.content);
    } else {
      setEditingTemplate(null);
      setTemplateTitle("");
      setTemplateContent("");
    }
    setIsTemplateModalOpen(true);
  };

  // Função para abrir modal de edição de categoria
  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  // Função para abrir modal de criar subcategoria
  const handleOpenSubCategoryModal = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSubCategoryName("");
    setSubCategoryDescription("");
    setIsSubCategoryModalOpen(true);
  };

  // Função para criar subcategoria
  const handleSubmitSubCategory = async () => {
    if (!subCategoryName.trim()) {
      toast.error("O nome da subcategoria é obrigatório");
      return;
    }

    if (!selectedCategoryId) {
      toast.error("Categoria não selecionada");
      return;
    }

    try {
      await createSubCategoryMutation.mutateAsync({
        categoryId: selectedCategoryId,
        name: subCategoryName,
        description: subCategoryDescription,
      });

      toast.success("Subcategoria criada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["getCategories"] });

      setIsSubCategoryModalOpen(false);
      setSubCategoryName("");
      setSubCategoryDescription("");
      setSelectedCategoryId("");
    } catch (error) {
      toast.error("Erro ao criar subcategoria");
      console.error(error);
    }
  };

  // Função para deletar categoria
  const handleDeleteCategory = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setCategoryDeleteConfirmOpen(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteCategoryMutation.mutateAsync(categoryToDelete);
      toast.success("Categoria removida com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["getCategories"] });
      setCategoryDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      toast.error("Erro ao remover categoria");
      console.error(error);
    }
  };

  // Função para deletar subcategoria
  const handleDeleteSubcategory = (subcategoryId: string) => {
    setSubcategoryToDelete(subcategoryId);
    setSubcategoryDeleteConfirmOpen(true);
  };

  const confirmDeleteSubcategory = async () => {
    if (!subcategoryToDelete) return;

    try {
      await deleteSubCategoryMutation.mutateAsync(subcategoryToDelete);
      toast.success("Subcategoria removida com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["getCategories"] });
      setSubcategoryDeleteConfirmOpen(false);
      setSubcategoryToDelete(null);
    } catch (error) {
      toast.error("Erro ao remover subcategoria");
      console.error(error);
    }
  };

  const handleCloseTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplate(null);
    setTemplateTitle("");
    setTemplateContent("");
  };

  const handleSubmitTemplate = async () => {
    if (!templateTitle.trim()) {
      toast.error("O título do template é obrigatório");
      return;
    }
    if (!templateContent.trim()) {
      toast.error("O conteúdo do template é obrigatório");
      return;
    }
    if (templateContent.trim().length < 10) {
      toast.error("O conteúdo deve ter no mínimo 10 caracteres");
      return;
    }

    try {
      if (editingTemplate) {
        await updateTemplateMutation.mutateAsync({
          id: editingTemplate.id,
          title: templateTitle,
          content: templateContent,
        });
        toast.success("Template atualizado com sucesso!");
      } else {
        await createTemplateMutation.mutateAsync({
          title: templateTitle,
          content: templateContent,
        });
        toast.success("Template criado com sucesso!");
      }

      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      handleCloseTemplateModal();
    } catch (error) {
      toast.error("Erro ao salvar template");
      console.error(error);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    setTemplateToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await deleteTemplateMutation.mutateAsync(templateToDelete);
      toast.success("Template excluído com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      toast.error("Erro ao excluir template");
      console.error(error);
    }
  };

  return (
    <>
      <HeaderPage
        title="Configurações"
        description="Gerencie as configurações do sistema"
      />

      <div className="mt-6">
        <Tabs defaultValue="geral" className="space-y-4">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="geral">Geral</TabsTrigger>
            <TabsTrigger value="categorias">Categorias</TabsTrigger>
            <TabsTrigger value="templates">Templates de Mensagens</TabsTrigger>
          </TabsList>

          <TabsContent value="geral" className="space-y-6">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsHistoryDialogOpen(true)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <History className="h-4 w-4" />
                Ver Histórico
              </Button>
            </div>

            {loadingConfiguration ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <Loading label="Carregando configurações..." />
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmitConfiguration)}>
                {!configurationData && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <p className="text-sm font-medium text-blue-900">
                        Nenhuma configuração encontrada
                      </p>
                    </div>
                    <p className="text-sm text-blue-700 mt-1">
                      Preencha os dados abaixo para criar a configuração inicial
                      do sistema.
                    </p>
                  </div>
                )}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="bg-white">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        <CardTitle className="text-base">
                          Informações do Município
                        </CardTitle>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Configure as informações básicas do seu município
                      </p>
                    </CardHeader>
                    <CardContentUI className="space-y-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="municipalityName" className="text-sm">
                            Nome do Município{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="municipalityName"
                            {...register("municipalityName")}
                            placeholder="Digite o nome do município"
                          />
                          {errors.municipalityName && (
                            <p className="text-sm text-destructive">
                              {errors.municipalityName.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="municipalityState"
                            className="text-sm"
                          >
                            Estado <span className="text-destructive">*</span>
                          </Label>
                          <Select
                            key={
                              configurationData?.municipalityState || "empty"
                            }
                            defaultValue={
                              configurationData?.municipalityState || ""
                            }
                            onValueChange={(value) => {
                              console.log("🔄 Mudando estado para:", value);
                              setValue("municipalityState", value, {
                                shouldValidate: true,
                              });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o estado" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AC">Acre</SelectItem>
                              <SelectItem value="AL">Alagoas</SelectItem>
                              <SelectItem value="AP">Amapá</SelectItem>
                              <SelectItem value="AM">Amazonas</SelectItem>
                              <SelectItem value="BA">Bahia</SelectItem>
                              <SelectItem value="CE">Ceará</SelectItem>
                              <SelectItem value="DF">
                                Distrito Federal
                              </SelectItem>
                              <SelectItem value="ES">Espírito Santo</SelectItem>
                              <SelectItem value="GO">Goiás</SelectItem>
                              <SelectItem value="MA">Maranhão</SelectItem>
                              <SelectItem value="MT">Mato Grosso</SelectItem>
                              <SelectItem value="MS">
                                Mato Grosso do Sul
                              </SelectItem>
                              <SelectItem value="MG">Minas Gerais</SelectItem>
                              <SelectItem value="PA">Pará</SelectItem>
                              <SelectItem value="PB">Paraíba</SelectItem>
                              <SelectItem value="PR">Paraná</SelectItem>
                              <SelectItem value="PE">Pernambuco</SelectItem>
                              <SelectItem value="PI">Piauí</SelectItem>
                              <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                              <SelectItem value="RN">
                                Rio Grande do Norte
                              </SelectItem>
                              <SelectItem value="RS">
                                Rio Grande do Sul
                              </SelectItem>
                              <SelectItem value="RO">Rondônia</SelectItem>
                              <SelectItem value="RR">Roraima</SelectItem>
                              <SelectItem value="SC">Santa Catarina</SelectItem>
                              <SelectItem value="SP">São Paulo</SelectItem>
                              <SelectItem value="SE">Sergipe</SelectItem>
                              <SelectItem value="TO">Tocantins</SelectItem>
                            </SelectContent>
                          </Select>
                          {errors.municipalityState && (
                            <p className="text-sm text-destructive">
                              {errors.municipalityState.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="estimatedPopulation"
                            className="text-sm"
                          >
                            População Estimada{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="estimatedPopulation"
                            type="number"
                            {...register("estimatedPopulation", {
                              valueAsNumber: true,
                            })}
                            placeholder="Ex: 150000"
                          />
                          {errors.estimatedPopulation && (
                            <p className="text-sm text-destructive">
                              {errors.estimatedPopulation.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="contactEmail" className="text-sm">
                            E-mail de Contato{" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="contactEmail"
                            type="email"
                            {...register("contactEmail")}
                            placeholder="contato@municipio.gov.br"
                          />
                          {errors.contactEmail && (
                            <p className="text-sm text-destructive">
                              {errors.contactEmail.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContentUI>
                  </Card>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="mt-6"
                >
                  <Card className="bg-white">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <CardTitle className="text-base">
                          Configurações de SLA
                        </CardTitle>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Defina os prazos de atendimento por categoria e
                        prioridade
                      </p>
                    </CardHeader>
                    <CardContentUI className="space-y-4">
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">
                          Prazos por Prioridade (em dias)
                        </h4>
                        <div className="grid gap-3 md:grid-cols-4">
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="slaCriticalDays"
                              className="text-red-600 text-sm"
                            >
                              Crítica{" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="slaCriticalDays"
                              type="number"
                              {...register("slaCriticalDays", {
                                valueAsNumber: true,
                              })}
                            />
                            {errors.slaCriticalDays && (
                              <p className="text-sm text-destructive">
                                {errors.slaCriticalDays.message}
                              </p>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="slaHighDays"
                              className="text-orange-600 text-sm"
                            >
                              Alta <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="slaHighDays"
                              type="number"
                              {...register("slaHighDays", {
                                valueAsNumber: true,
                              })}
                            />
                            {errors.slaHighDays && (
                              <p className="text-sm text-destructive">
                                {errors.slaHighDays.message}
                              </p>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="slaMediumDays"
                              className="text-yellow-600 text-sm"
                            >
                              Média <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="slaMediumDays"
                              type="number"
                              {...register("slaMediumDays", {
                                valueAsNumber: true,
                              })}
                            />
                            {errors.slaMediumDays && (
                              <p className="text-sm text-destructive">
                                {errors.slaMediumDays.message}
                              </p>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="slaLowDays"
                              className="text-green-600 text-sm"
                            >
                              Baixa <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="slaLowDays"
                              type="number"
                              {...register("slaLowDays", {
                                valueAsNumber: true,
                              })}
                            />
                            {errors.slaLowDays && (
                              <p className="text-sm text-destructive">
                                {errors.slaLowDays.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">
                          Configurações de Deduplicação
                        </h4>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="deduplicationRadiusMeters"
                              className="text-sm"
                            >
                              Raio de detecção (metros){" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="deduplicationRadiusMeters"
                              type="number"
                              {...register("deduplicationRadiusMeters", {
                                valueAsNumber: true,
                              })}
                            />
                            {errors.deduplicationRadiusMeters && (
                              <p className="text-sm text-destructive">
                                {errors.deduplicationRadiusMeters.message}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Distância máxima para considerar ocorrências como
                              duplicadas
                            </p>
                          </div>
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="deduplicationTimeWindowDays"
                              className="text-sm"
                            >
                              Janela de tempo (dias){" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="deduplicationTimeWindowDays"
                              type="number"
                              {...register("deduplicationTimeWindowDays", {
                                valueAsNumber: true,
                              })}
                            />
                            {errors.deduplicationTimeWindowDays && (
                              <p className="text-sm text-destructive">
                                {errors.deduplicationTimeWindowDays.message}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Período para verificar duplicatas
                            </p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">
                          Reabertura de Ocorrências
                        </h4>
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="occurrenceReopeningDeadlineDays"
                            className="text-sm"
                          >
                            Prazo para reabertura (dias){" "}
                            <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="occurrenceReopeningDeadlineDays"
                            type="number"
                            {...register("occurrenceReopeningDeadlineDays", {
                              valueAsNumber: true,
                            })}
                            className="max-w-xs"
                          />
                          {errors.occurrenceReopeningDeadlineDays && (
                            <p className="text-sm text-destructive">
                              {errors.occurrenceReopeningDeadlineDays.message}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Tempo que o cidadão tem para reabrir uma ocorrência
                            encerrada
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          className="text-white cursor-pointer"
                          disabled={
                            createConfigurationMutation.isPending ||
                            updateConfigurationMutation.isPending
                          }
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {createConfigurationMutation.isPending ||
                          updateConfigurationMutation.isPending
                            ? "Salvando..."
                            : "Salvar Configurações"}
                        </Button>
                      </div>
                    </CardContentUI>
                  </Card>
                </motion.div>
              </form>
            )}
          </TabsContent>

          <TabsContent value="categorias" className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-primary" />
                        <CardTitle className="text-base">
                          Categorias de Ocorrências
                        </CardTitle>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Configure as categorias e subcategorias disponíveis para
                        reportes
                      </p>
                    </div>
                    <Button
                      onClick={() => setIsModalOpen(true)}
                      className="text-white cursor-pointer"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Categoria
                    </Button>
                  </div>
                </CardHeader>
                <CardContentUI>
                  {loadingCategories ? (
                    <div className="flex items-center justify-center py-8">
                      <p className="text-muted-foreground">
                        Carregando categorias...
                      </p>
                    </div>
                  ) : !categories || categories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <p className="text-muted-foreground mb-2">
                        Nenhuma categoria cadastrada
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Clique em "Adicionar Categoria" para criar a primeira
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {categories.map((category: Category) => (
                        <motion.div
                          key={category.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="group p-4 border rounded-lg bg-gradient-to-br from-white to-gray-50/50 hover:shadow-md transition-all duration-200"
                        >
                          {/* Cabeçalho da Categoria */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start gap-3 flex-1">
                              <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                                <CategoryIcon
                                  iconName={category.icon}
                                  iconLibrary={category.lib}
                                  className="h-4 w-4 text-primary"
                                />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-base text-gray-900">
                                    {category.name}
                                  </h4>
                                  <Badge variant="outline" className="text-xs">
                                    {category.subcategories?.length || 0}{" "}
                                    subcategorias
                                  </Badge>
                                </div>
                                {category.description && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {category.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="cursor-pointer hover:bg-primary/5"
                                onClick={() => handleEditCategory(category)}
                              >
                                <Edit className="h-3.5 w-3.5 mr-1.5" />
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleDeleteCategory(category.id)
                                }
                                className="text-destructive hover:bg-destructive/5 cursor-pointer"
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                Remover
                              </Button>
                            </div>
                          </div>

                          {/* Subcategorias */}
                          {category.subcategories &&
                          category.subcategories.length > 0 ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium text-muted-foreground">
                                    Subcategorias
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleOpenSubCategoryModal(category.id)
                                  }
                                  className="cursor-pointer h-8"
                                >
                                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                                  Adicionar Subcategoria
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 pl-6">
                                {category.subcategories.map((sub) => (
                                  <div
                                    key={sub.id}
                                    className="group/sub flex items-start gap-2 p-3 bg-white border border-gray-200 rounded-md hover:border-primary/50 transition-colors relative"
                                  >
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {sub.name}
                                      </p>
                                      {sub.description && (
                                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                          {sub.description}
                                        </p>
                                      )}
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleDeleteSubcategory(sub.id)
                                      }
                                      className="opacity-0 group-hover/sub:opacity-100 transition-opacity absolute top-1 right-1 h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm text-muted-foreground italic">
                                    Nenhuma subcategoria cadastrada
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleOpenSubCategoryModal(category.id)
                                  }
                                  className="cursor-pointer h-8"
                                >
                                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                                  Adicionar Subcategoria
                                </Button>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContentUI>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Aba de Templates de Mensagens */}
          <TabsContent value="templates" className="space-y-6">
            {loadingTemplates ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <Loading label="Carregando templates..." />
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="bg-white">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-primary" />
                        <CardTitle className="text-base">
                          Templates de Mensagens
                        </CardTitle>
                      </div>
                      <Button
                        onClick={() => handleOpenTemplateModal()}
                        size="sm"
                        className="text-white cursor-pointer"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Novo Template
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Crie templates de mensagens reutilizáveis para seus
                      comentários
                    </p>
                  </CardHeader>
                  <CardContentUI>
                    {!templates || templates.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-gray-300 rounded-lg">
                        <MessageSquare className="h-12 w-12 text-gray-400 mb-3" />
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          Nenhum template cadastrado
                        </p>
                        <p className="text-xs text-muted-foreground text-center mb-4 max-w-sm">
                          Crie templates de mensagens para facilitar a
                          comunicação com os cidadãos
                        </p>
                        <Button
                          onClick={() => handleOpenTemplateModal()}
                          variant="outline"
                          size="sm"
                          className="cursor-pointer"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Criar Primeiro Template
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {templates.map((template: any) => (
                          <motion.div
                            key={template.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 border border-gray-200 rounded-lg hover:border-primary/50 transition-all bg-white"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <MessageSquare className="h-4 w-4 text-primary flex-shrink-0" />
                                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                                    {template.title}
                                  </h3>
                                </div>
                                <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                                  {template.content}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleOpenTemplateModal(template)
                                  }
                                  className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 cursor-pointer"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteTemplate(template.id)
                                  }
                                  className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 cursor-pointer"
                                  disabled={deleteTemplateMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </CardContentUI>
                </Card>
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Template de Mensagem */}
      <Dialog open={isTemplateModalOpen} onOpenChange={setIsTemplateModalOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              {editingTemplate ? "Editar Template" : "Novo Template"}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate
                ? "Atualize as informações do template de mensagem"
                : "Crie um novo template de mensagem reutilizável"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto max-h-[calc(85vh-200px)]">
            <div className="space-y-2">
              <Label htmlFor="templateTitle">
                Título do Template <span className="text-destructive">*</span>
              </Label>
              <Input
                id="templateTitle"
                placeholder="Ex: Boas-vindas"
                value={templateTitle}
                onChange={(e) => setTemplateTitle(e.target.value)}
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="templateContent">
                Conteúdo da Mensagem <span className="text-destructive">*</span>
              </Label>
              
              {/* Aviso de variáveis disponíveis */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Personalize sua mensagem com variáveis dinâmicas:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-blue-600" />
                    <code className="text-xs font-mono font-semibold text-blue-700 bg-white px-2 py-0.5 rounded shadow-sm">
                      {"{{USERNAME}}"}
                    </code>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-blue-600" />
                    <code className="text-xs font-mono font-semibold text-blue-700 bg-white px-2 py-0.5 rounded shadow-sm">
                      {"{{PROTOCOL}}"}
                    </code>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-blue-600" />
                    <code className="text-xs font-mono font-semibold text-blue-700 bg-white px-2 py-0.5 rounded shadow-sm">
                      {"{{ADDRESS}}"}
                    </code>
                  </div>
                </div>
              </div>

              {/* Botões de inserção rápida */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium text-gray-700">
                  Clique para inserir no texto:
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    onClick={() => {
                      const textarea = document.getElementById("templateContent") as HTMLTextAreaElement;
                      const cursorPos = textarea.selectionStart;
                      const textBefore = templateContent.substring(0, cursorPos);
                      const textAfter = templateContent.substring(cursorPos);
                      setTemplateContent(textBefore + "{{USERNAME}}" + textAfter);
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(cursorPos + 12, cursorPos + 12);
                      }, 0);
                    }}
                  >
                    <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                    <User className="h-3 w-3 mr-1" />
                    Inserir Nome
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    onClick={() => {
                      const textarea = document.getElementById("templateContent") as HTMLTextAreaElement;
                      const cursorPos = textarea.selectionStart;
                      const textBefore = templateContent.substring(0, cursorPos);
                      const textAfter = templateContent.substring(cursorPos);
                      setTemplateContent(textBefore + "{{PROTOCOL}}" + textAfter);
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(cursorPos + 12, cursorPos + 12);
                      }, 0);
                    }}
                  >
                    <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                    <FileText className="h-3 w-3 mr-1" />
                    Inserir Protocolo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    onClick={() => {
                      const textarea = document.getElementById("templateContent") as HTMLTextAreaElement;
                      const cursorPos = textarea.selectionStart;
                      const textBefore = templateContent.substring(0, cursorPos);
                      const textAfter = templateContent.substring(cursorPos);
                      setTemplateContent(textBefore + "{{ADDRESS}}" + textAfter);
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(cursorPos + 11, cursorPos + 11);
                      }, 0);
                    }}
                  >
                    <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
                    <MapPin className="h-3 w-3 mr-1" />
                    Inserir Endereço
                  </Button>
                </div>
              </div>

              <textarea
                id="templateContent"
                placeholder="Digite o conteúdo do template..."
                value={templateContent}
                onChange={(e) => setTemplateContent(e.target.value)}
                rows={12}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
              
              <p className="text-xs text-muted-foreground">
                {templateContent.length} caracteres
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseTemplateModal}
              className="cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitTemplate}
              disabled={
                createTemplateMutation.isPending ||
                updateTemplateMutation.isPending
              }
              className="text-white cursor-pointer"
            >
              {createTemplateMutation.isPending ||
              updateTemplateMutation.isPending
                ? "Salvando..."
                : editingTemplate
                  ? "Atualizar"
                  : "Criar Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Adicionar Categoria */}
      <CategoryModal
        open={isModalOpen}
        editingCategory={editingCategory}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCategory(null);
        }}
        onSaved={() => {
          setIsModalOpen(false);
          setEditingCategory(null);
        }}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Excluir Template"
        description="Tem certeza que deseja excluir este template de mensagem? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDeleteTemplate}
        variant="destructive"
        isLoading={deleteTemplateMutation.isPending}
      />

      {/* Dialog de Histórico de Configurações */}
      <ConfigurationHistoryDialog
        open={isHistoryDialogOpen}
        onOpenChange={setIsHistoryDialogOpen}
      />

      {/* Modal de Adicionar Subcategoria */}
      <Dialog
        open={isSubCategoryModalOpen}
        onOpenChange={setIsSubCategoryModalOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar Subcategoria</DialogTitle>
            <DialogDescription>
              Crie uma nova subcategoria para esta categoria de ocorrência
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subCatName">
                Nome da Subcategoria <span className="text-destructive">*</span>
              </Label>
              <Input
                id="subCatName"
                placeholder="Ex: Buraco no asfalto"
                value={subCategoryName}
                onChange={(e) => setSubCategoryName(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                {subCategoryName.length}/100 caracteres
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subCatDescription">Descrição (opcional)</Label>
              <textarea
                id="subCatDescription"
                placeholder="Buracos, crateras e irregularidades no asfalto..."
                value={subCategoryDescription}
                onChange={(e) => setSubCategoryDescription(e.target.value)}
                maxLength={255}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
              <p className="text-xs text-muted-foreground">
                {subCategoryDescription.length}/255 caracteres
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsSubCategoryModalOpen(false);
                setSubCategoryName("");
                setSubCategoryDescription("");
                setSelectedCategoryId("");
              }}
              className="cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitSubCategory}
              disabled={createSubCategoryMutation.isPending}
              className="text-white cursor-pointer"
            >
              {createSubCategoryMutation.isPending
                ? "Salvando..."
                : "Criar Subcategoria"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão de Categoria */}
      <ConfirmDialog
        open={categoryDeleteConfirmOpen}
        onOpenChange={setCategoryDeleteConfirmOpen}
        title="Remover Categoria"
        description="Tem certeza que deseja remover esta categoria? Todas as subcategorias associadas também serão removidas. Esta ação não pode ser desfeita."
        confirmText="Remover"
        cancelText="Cancelar"
        onConfirm={confirmDeleteCategory}
        variant="destructive"
        isLoading={deleteCategoryMutation.isPending}
      />

      {/* Modal de Confirmação de Exclusão de Subcategoria */}
      <ConfirmDialog
        open={subcategoryDeleteConfirmOpen}
        onOpenChange={setSubcategoryDeleteConfirmOpen}
        title="Remover Subcategoria"
        description="Tem certeza que deseja remover esta subcategoria? Esta ação não pode ser desfeita."
        confirmText="Remover"
        cancelText="Cancelar"
        onConfirm={confirmDeleteSubcategory}
        variant="destructive"
        isLoading={deleteSubCategoryMutation.isPending}
      />
    </>
  );
};
